import { useState, useCallback, useEffect } from "react";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import DownloadIcon from "@styles/all-images/icons/download.svg";
import ListIcon from "@styles/all-images/icons/list-icon.svg";
import UnarchiveIcon from "@styles/all-images/icons/unarchived.svg";
import { useNavigate } from "react-router-dom";
import CustomTable, {
  TableColumn,
} from "@/components/common/table/CustomTable";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import dateHandler from "@/composables/dateUtil";
import { usePermissionState } from "@/providers/PermissionProvider";
import { RolesPermissionsPermissions } from "@/constants/permission-modules/base-permissions";

interface Role {
  id: number;
  name: string;
  permissions: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

const RolesAndPermissions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDate } = dateHandler();
  const [isArchiveView, setIsArchiveView] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [forceReset, setForceReset] = useState(false);
  const [columns, setColumns] = useState<TableColumn<Role>[]>([]);
  const { isPermitted } = usePermissionState();

  const handleSettingColumns = useCallback(() => {
    setColumns([
      {
        header: "Role ID",
        accessor: "id",
        sortable: true,
        sortKey: "id",
      },
      {
        header: "Role Name",
        accessor: "name",
        sortable: true,
        sortKey: "group_auth_group_extend__name",
      },
      {
        header: "Created Date",
        accessor: (row: Role) => formatDate(row.created_at),
        sortable: true,
        sortKey: "created_at",
      },
      // {
      //   header: "Created By",
      //   accessor: "created_by",
      //   sortable: true,
      //   sortKey: "created_by",
      // },
      {
        header: "Actions",
        accessor: (row: Role) => (
          <div className="tw-flex tw-items-center">
            {!isArchiveView ? (
              <>
                {isPermitted(
                  RolesPermissionsPermissions.UPDATE_ROLES_AND_PERMISSIONS,
                ) && (
                  <button onClick={() => handleEdit(row)} title="Edit">
                    <img
                      src={EditIcon}
                      alt="Edit Icon"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                )}
                {isPermitted(
                  RolesPermissionsPermissions.ARCHIVE_ROLES_AND_PERMISSIONS,
                ) && (
                  <button
                    onClick={() => handleArchiveClick(row)}
                    title="Archive"
                  >
                    <img
                      src={DownloadIcon}
                      alt="Archive Icon"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                )}
              </>
            ) : (
              isPermitted(
                RolesPermissionsPermissions.ARCHIVE_ROLES_AND_PERMISSIONS,
              ) && (
                <button
                  onClick={() => handleUnarchiveClick(row)}
                  title="Unarchive"
                >
                  <img
                    src={UnarchiveIcon}
                    alt="Unarchive Icon"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              )
            )}
          </div>
        ),
      },
    ]);
  }, [isArchiveView]);

  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      try {
        const { getAllData } = actionsHandler("roles");
        const customQuery = `is_archive=${isArchiveView}${
          params.sortColumn
            ? `&ordering=${params.sortDirection === "asc" ? "" : "-"}${
                params.sortColumn
              }`
            : ""
        }`;
        const response = await dispatch(
          getAllData({
            limit: params.limit || 10,
            page: params.page || 1,
            search: params.search || "",
            customQuery,
          }),
        );
        return response;
      } catch (error) {
        console.error("Error fetching roles data:", error);
        return {};
      }
    },
    [dispatch, isArchiveView],
  );

  const handleEdit = (role: Role) => {
    navigate(`/edit-add-roles-and-permissions/${role.id}`);
  };

  const handleArchiveClick = (role: Role) => {
    setSelectedRole(role);
    setShowConfirmDialog(true);
  };

  const handleUnarchiveClick = (role: Role) => {
    setSelectedRole(role);
    setShowConfirmDialog(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedRole) return;

    try {
      const { authenticatedCall } = actionsHandler("roleWithoutSlash");
      const response = await dispatch(
        authenticatedCall({
          method: "patch",
          customParams: [String(selectedRole.id) + "/"],
          payload: {
            is_archive: !isArchiveView,
          },
        }),
      );
      toast.success(response.data.message);
      setForceRefresh(true);
    } catch (error: any) {
      toast.error(
        error.message ||
          `Failed to ${isArchiveView ? "unarchive" : "archive"} role`,
      );
    } finally {
      setShowConfirmDialog(false);
      setSelectedRole(null);
    }
  };

  useEffect(() => {
    handleSettingColumns();
    setForceReset(true);
  }, [isArchiveView]);

  return (
    <div>
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <img
                src={BackIcon}
                alt="Back Icon"
                className="tw-cursor-pointer"
                onClick={() => navigate("/menu")}
              />
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Roles and Permissions
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-mt-[147px]">
        <CustomTable
          columns={columns}
          fetchData={handleFetch}
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
          forceReset={forceReset}
          setForceReset={setForceReset}
          addButton={
            isPermitted(
              RolesPermissionsPermissions.CREATE_ROLES_AND_PERMISSIONS,
            )
              ? {
                  text: "Add Role",
                  icon: PlusIcon,
                  onClick: () => navigate("/edit-add-roles-and-permissions"),
                }
              : undefined
          }
          archiveButton={{
            text: isArchiveView ? "Back to List" : "Archived",
            icon: isArchiveView ? ListIcon : DownloadIcon,
            onClick: () => setIsArchiveView(!isArchiveView),
            isArchiveView,
          }}
          noDataMessage={`No roles found. Please add a new role to proceed.`}
          noSearchDataMessage={`No matching role found`}
        />
      </div>

      {showConfirmDialog && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">
                Confirm {isArchiveView ? "Unarchive" : "Archive"}
              </h2>
              <button onClick={() => setShowConfirmDialog(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-flex-col tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold">
                Are you sure you want to{" "}
                {isArchiveView ? "unarchive" : "archive"} this role?
              </h6>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesAndPermissions;
